import {
  UTC,
  isNullOrUndefined,
  TimeSpan,
  TimestampRange,
  guid,
  isNullOrUndefinedOrEmpty,
  utc,
}                              from 'rewire-common';
import { defaultEquals }       from 'rewire-core';
import { getNumberString }     from 'rewire-grid';
import {
  ITimecard,
  IJob,
  IOccupation,
  ITemplate,
  IGroup,
  ITimecardTemplate,
  IEmployee,
  lookupKeyFromUdfDocumentType,
  FieldFilter,
  UdfDocumentType,
  TimecardInput,
  numberColumn,
  isMultiple,
  IReason,
}                              from '../DataTypes';
import { localTimeZoneUTCNow } from './DateUtils';

export function dataUrlToFile(dataUrl: string, fileName: string) {
  const arr   = dataUrl.split(',');
  const mime  = arr[0].match(/:(.*?);/)?.[1];
  const bstr  = atob(arr[1]);
  let   n     = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, {type: mime});
}

export function roundTo(num: number, decimalPlaces = 2): number {
  return +(Math.round(parseFloat(num + `e+${decimalPlaces}`)) + `e-${decimalPlaces}`);
}

export function numericTimeToTimestampRange(start?: number, end?: number, earnedDate?: UTC): TimestampRange | undefined {
  const startUTC = earnedDate && !isNullOrUndefined(start) ? earnedDate.clone().startOfDay().add(start, TimeSpan.hours, 4) : undefined;
  const endUTC   = earnedDate && !isNullOrUndefined(end)   ? earnedDate.clone().startOfDay().add(end, TimeSpan.hours, 4)   : undefined;
  return startUTC && endUTC && new TimestampRange(startUTC, endUTC);
}

export function timestampRangeToNumericTime(effective?: TimestampRange, earnedDate?: UTC): {start?: number | string, end?: number | string} {
  const eDate     = earnedDate?.clone().startOfDay();
  const startTime = eDate && effective ? effective.start.subtract(eDate, TimeSpan.hours, 4) : effective ? effective.startAsTimeString() : undefined;
  const endTime   = eDate && effective ? effective.end.subtract(eDate, TimeSpan.hours, 4)   : effective ? effective.endAsTimeString()   : undefined;
  return {start: startTime, end: endTime};
}

export function combineTimestampRanges(r1: TimestampRange, r2: TimestampRange): TimestampRange | undefined {
  if (!r1 || !r2 || (!r1.inRange(r2.start) && !r1.inRange(r2.end) && !r2.inRange(r1.start) && !r2.inRange(r1.end))) {
    return undefined;
  }

  const newStart = r1.start <= r2.start ? r1.start : r2.start;
  const newEnd   = r1.end >= r2.end ? r1.end : r2.end;
  return new TimestampRange(newStart, newEnd);
}

export function timecardToTimecardInput(timecard: ITimecard): TimecardInput {
  const timecardInput: TimecardInput = {
    id:               timecard.id,
    employeeId:       timecard.employee?.id,
    earnedDate:       timecard.earnedDate,
    reasonId:         timecard.reason?.id,
    overtimeReasonId: timecard.overtimeReason?.id,
    groupId:          timecard.group?.id,
    occupationId:     timecard.occupation?.id,
    jobId:            timecard.job?.id,
    effective:        timecard.effective,
    units:            timecard.units,
    rate:             timecard.rate,
    factor:           timecard.factor,
    amount:           timecard.amount,
    accountCode:      timecard.accountCode,
    createdBy:        timecard.createdBy,
    createdDate:      timecard.createdDate ?? UTC.now(),
    tags:             timecard.tags,
    fields:           timecard.fields,
    shift:            timecard.shift,
    templateId:       timecard.template?.id,
  };
  return timecardInput;
}

export function timecardTemplateToTimecard( data: { timecardTemplate: ITimecardTemplate, template?: ITemplate, earnedDate?: UTC, group?: IGroup, job?: IJob, occupation?: IOccupation, employee?: IEmployee, reason?: IReason } ): ITimecard {
  const earnedDate       = data.earnedDate ?? localTimeZoneUTCNow().startOfDay();
  const timecardTemplate = data.timecardTemplate;
  const hasTimes         = !isNullOrUndefined(timecardTemplate.start) && !isNullOrUndefined(timecardTemplate.end);

  const timecard: ITimecard = {
    id:             guid(),
    earnedDate:     earnedDate,
    effective:      hasTimes ? numericTimeToTimestampRange(timecardTemplate.start, timecardTemplate.end, earnedDate) : undefined,
    accountCode:    timecardTemplate.accountCode,
    units:          timecardTemplate.units,
    factor:         timecardTemplate.factor,
    rate:           timecardTemplate.rate,
    amount:         timecardTemplate.amount,
    template:       data.template,
    createdBy:      undefined,
    createdDate:    UTC.now(),
    tags:           undefined,
    job:            data.job,
    employee:       data.employee,
    reason:         data.reason ?? timecardTemplate.reason,
    overtimeReason: timecardTemplate.overtimeReason,
    generated:      undefined,
    group:          data.group,
    batch:          undefined,
    fields:         undefined,
    shift:          0,
    occupation:     data.occupation,
  };
  return timecard;
}

export function reasonCategoryDataMapper(reason: any) {
  reason.category = reason.category ?? reason.other?.category;
  if (reason.other) {
    delete reason.other;
  }
  return reason;
}

export const arrayMapper         = (a: any) => Array.isArray(a) ? a.join(', ') : a;
export const booleanMapper       = (b: any) => b ? 'Yes' : 'No';
export const nameMapper          = (v: any) => v?.name;
export const codeMapper          = (x: any) => x?.code;
export const dateMapper          = (d: any) => d ? (d.toDateString?.() ?? utc(d).toDateString()) : undefined;
export const dateTimeMapper      = (d: any) => d ? (d.toDateTime?.() ?? utc(d).toTimestampString()) : undefined;
export const numberStringMapper  = (v: any) => getNumberString(v, numberColumn.options.decimals, numberColumn.options.thousandSeparator, numberColumn.options.fixed);
export const getDifferenceMapper = (mapper?: (v: any) => string | undefined | null | (string | undefined | null)[]) => (value: {value1: any, value2: any}) => ({value1: mapper?.(value?.value1) ?? value?.value1, value2: mapper?.(value?.value2) ?? value?.value2});

export const getDifferenceCellTooltip = (value: {value1: any, value2: any}) => {
  if ((isMultiple(value.value1) && isMultiple(value.value2)) || defaultEquals(value.value1, value.value2)) {
    return `${value.value1}`;
  } else {
    const value1 = isNullOrUndefinedOrEmpty(value.value1) ? '«empty»' : value.value1;
    const value2 = isNullOrUndefinedOrEmpty(value.value2) ? '«empty»' : value.value2;
    return `${value1} --> ${value2}`;
  }
};

export function getUdfFilterLookupField(fieldFilter: FieldFilter, documentType: UdfDocumentType): FieldFilter {
  fieldFilter       = {...fieldFilter};
  const type        = fieldFilter.field.split(':')[1];
  const lookupType  = lookupKeyFromUdfDocumentType(documentType);
  let lookupString  = `${lookupType}.fields.${fieldFilter.field}`;
  if (type === 'r') {
    lookupString = `${lookupString}.id`;
    if (fieldFilter.in?.length) {
      fieldFilter.in = fieldFilter.in.map((value: any) => value.id);
    }
  }

  fieldFilter.field = lookupString;
  return fieldFilter;
}

export function getUdfSortLookupString(fieldName: string, documentType: UdfDocumentType): string {
  const type        = fieldName.split(':')[1];
  const lookupType  = lookupKeyFromUdfDocumentType(documentType);
  let lookupString  = `${lookupType}.fields.${fieldName}`;
  if (type === 'r') {
    lookupString = `${lookupString}.name`;
  }

  return lookupString;
}
export function detailsAreTimesAndUnitsCompatible(details1: ITimecard[], details2: ITimecard[]): boolean {
  if (details1.length !== details2!.length) {
    return false;
  }
  for (let i = 0; i < details1.length; i++) {
    const detailsTimecard1 = details1[i];
    const detailsTimecard2 = details2[i];
    if ((!detailsTimecard1.effective && detailsTimecard2.effective) || (detailsTimecard1.effective && !detailsTimecard2.effective || detailsTimecard1.units !== detailsTimecard2.units)) {
      return false;
    }
    if (detailsTimecard1.effective && detailsTimecard2.effective) {
      const detailsTimecard1Times = timestampRangeToNumericTime(detailsTimecard1.effective, detailsTimecard1.earnedDate);
      const detailsTimecard2Times = timestampRangeToNumericTime(detailsTimecard2.effective, detailsTimecard2.earnedDate);
      if (detailsTimecard1Times.start !== detailsTimecard2Times.start || detailsTimecard1Times.end !== detailsTimecard2Times.end) {
        return false;
      }
    }
  }
  return true;
}

export function detailsAreFullyCompatible(details1: ITimecard[], details2: ITimecard[]): boolean {
  if (details1.length !== details2!.length) {
    return false;
  }
  for (let i = 0; i < details1.length; i++) {
    const detailsTimecard1 = details1[i];
    const detailsTimecard2 = details2[i];
    if ((!detailsTimecard1.effective && detailsTimecard2.effective) || (detailsTimecard1.effective && !detailsTimecard2.effective || detailsTimecard1.units !== detailsTimecard2.units)) {
      return false;
    }
    if (detailsTimecard1.effective && detailsTimecard2.effective) {
      const detailsTimecard1Times = timestampRangeToNumericTime(detailsTimecard1.effective, detailsTimecard1.earnedDate);
      const detailsTimecard2Times = timestampRangeToNumericTime(detailsTimecard2.effective, detailsTimecard2.earnedDate);
      if (detailsTimecard1Times.start !== detailsTimecard2Times.start || detailsTimecard1Times.end !== detailsTimecard2Times.end) {
        return false;
      }
    }
    if (!defaultEquals(details1[i].reason, details2[i].reason)) {
      return false;
    }
  }
  return true;
}

export function detailsAreContiguous(details: ITimecard[]): boolean {
  // assumes details are sorted by effective already
  const detailsWithTimes               = details.filter((d: ITimecard) => d.effective?.start && d.effective?.end);
  let lastDetailEnd: UTC | undefined = undefined;
  for (let i = 0; i < detailsWithTimes.length; i++) {
    const detail      = detailsWithTimes[i];
    const detailStart = detail.effective!.start;
    const detailEnd   = detail.effective!.end;
    if (lastDetailEnd && !lastDetailEnd.equals(detailStart)) {
      return false;
    }
    lastDetailEnd = detailEnd;
  }

  return true;
}

export function detailsAreOverlapping(details: ITimecard[]): boolean {
  // assumes details are sorted by effective already
  const detailsWithTimes                                = details.filter((d: ITimecard) => d.effective?.start && d.effective?.end);
  let lastDetailEffective: TimestampRange | undefined = undefined;
  for (let i = 0; i < detailsWithTimes.length; i++) {
    const detail          = detailsWithTimes[i];
    const detailEffective = detail.effective!;
    if (lastDetailEffective?.intersects(detailEffective)) {
      return true;
    }
    lastDetailEffective = detailEffective;
  }

  return false;
}

export function getGapDetails(details: ITimecard[]): ITimecard[] {
  // assumes details are sorted by effective already
  const detailsWithTimes                = details.filter((d: ITimecard) => d.effective?.start && d.effective?.end);
  const gaps                            = [] as ITimecard[];
  let lastDetail: ITimecard | undefined = undefined;
  detailsWithTimes.forEach(dwt => {
    const detailStart = dwt.effective!.start;
    if (lastDetail && !lastDetail.effective!.end.equals(detailStart)) {
      gaps.push({...lastDetail, effective: new TimestampRange(lastDetail.effective!.end, detailStart)});
    }
    lastDetail = dwt;
  });

  return gaps;
}

function _hasTimes(detail: ITimecard | ITimecardTemplate) {
  if ('effective' in detail) {
    return !isNullOrUndefined(detail.effective);
  } else {
    return !isNullOrUndefined(detail.start) && !isNullOrUndefined(detail.end);
  }
}

function _getTimestampRange(detail: ITimecard | ITimecardTemplate, baseDate: UTC) {
  if ('effective' in detail) {
    return detail.effective?.clone();
  } else {
    return numericTimeToTimestampRange((detail as ITimecardTemplate).start, (detail as ITimecardTemplate).end, baseDate);
  }
}

function _getMaps(details: ITimecard[] | ITimecardTemplate[], baseDate: UTC) {
  const timeRangesMap = new Map<string, TimestampRange[]>();
  const unitsOnlyMap  = new Map<string, number>();

  details.forEach((detail) => {
    const mapKey = getDetailMapKey(detail);

    if (!_hasTimes(detail)) {
      // units only
      if (unitsOnlyMap.has(mapKey)) {
        unitsOnlyMap.set(mapKey, unitsOnlyMap.get(mapKey)! + detail.units!);
      } else {
        unitsOnlyMap.set(mapKey, detail.units!);
      }
    } else {
      const timeRange = _getTimestampRange(detail, baseDate)!;
      if (timeRangesMap.has(mapKey)) {
        const trs = timeRangesMap.get(mapKey)!;
        for (let i = 0; i < trs.length; i++) {
          const combinedTr = combineTimestampRanges(trs[i], timeRange);
          if (combinedTr) {
            trs[i] = combinedTr;
            break;
          }
          if (i === trs.length - 1) {
            trs.push(timeRange);
          }
        }
      } else {
        timeRangesMap.set(mapKey, [timeRange]);
      }
    }
  });

  return {timeRangesMap, unitsOnlyMap};
}

export function hasDetailsDifferentFromTemplate(details: ITimecard[], timecardTemplates?: ITimecardTemplate[], earnedDate?: UTC): boolean {
  const hasDetails           = !!details?.length;
  const hasTimecardTemplates = !!timecardTemplates?.length;
  if (!hasDetails) {
    return hasTimecardTemplates;
  } else if (!hasTimecardTemplates) {
    return hasDetails;
  }

  const baseDate = earnedDate ?? details[0].earnedDate;
  const {timeRangesMap: detailsTimeRangesMap,  unitsOnlyMap: detailsUnitsOnlyMap}  = _getMaps(details, baseDate);
  const {timeRangesMap: templateTimeRangesMap, unitsOnlyMap: templateUnitsOnlyMap} = _getMaps(timecardTemplates, baseDate);

  // Handle Times
  for (const [key, timeRanges] of detailsTimeRangesMap) {
    if (!templateTimeRangesMap.has(key)) {
      continue;
    }

    const matchingTemplateTimeRanges    = templateTimeRangesMap.get(key)!;
    const newTimeRanges                 = Array.from(TimestampRange.subtract(timeRanges, matchingTemplateTimeRanges));
    const newMatchingTemplateTimeRanges = Array.from(TimestampRange.subtract(matchingTemplateTimeRanges, timeRanges));

    if (!newTimeRanges.length) {
      detailsTimeRangesMap.delete(key);
    }
    if (!newMatchingTemplateTimeRanges.length) {
      templateTimeRangesMap.delete(key);
    }
  }

  // Handle Units Only
  for (const [key, units] of detailsUnitsOnlyMap) {
    if (!templateUnitsOnlyMap.has(key)) {
      continue;
    }

    const matchingTemplateUnitsOnly = templateUnitsOnlyMap.get(key)!;
    const newUnits                  = matchingTemplateUnitsOnly - units;
    if (newUnits > 0) {
      detailsUnitsOnlyMap.delete(key);
      templateUnitsOnlyMap.set(key, newUnits);
    } else if (newUnits < 0) {
      templateUnitsOnlyMap.delete(key);
      detailsUnitsOnlyMap.set(key, -newUnits);
    } else {
      detailsUnitsOnlyMap.delete(key);
      templateUnitsOnlyMap.delete(key);
    }
  }

  return !!(detailsTimeRangesMap.size || detailsUnitsOnlyMap.size || templateTimeRangesMap.size || templateUnitsOnlyMap.size);
}

function getDetailMapKey(detail: ITimecard | ITimecardTemplate): string {
  const detailReasonKey   = detail.reason?.id ?? 'none';
  return `${detailReasonKey}`;
}
